// PrayerWeekCard — módulo visual da Semana Temática (Oração, Família, Cura, ...).
//
// Fontes:
//  - /api/integrations/prayer-week   → 7 dias (landing pode congelar aqui)
//  - /api/integrations/weekly-theme  → slug do tema atual (rotativo)
//  - useDaily().weekly_theme         → theme_name, description, week_start
//                                       (fonte para calcular datas e detectar
//                                       mismatch com o conteúdo dos dias)
//
// Iter59 — Fase C (fix Semana da Cura):
//  1. Detecção de MISMATCH: se theme_slug do weekly-theme não bate com o
//     conteúdo dos dias vindos de prayer-week, ativa OVERRIDE INTERNO.
//  2. Override: usa `getInternalThemeContent(themeSlug)` (weeklyThemeContent.js)
//     para renderizar 7 dias teologicamente alinhados ao tema.
//  3. Fallback anti-mistura: se mismatch E não temos override interno para o
//     tema → OCULTA a seção (nunca renderiza conteúdo teologicamente errado).
//  4. Datas + dia da semana: "Dia 1 · domingo, 19 de julho" calculado a partir
//     de week_start no timezone America/Recife.
//  5. Destaque gold border no dia atual.
//  6. Referências: `formatVerse` corrige "SI"→"Sl" e "Sl 34.15"→"Sl 34:15".
//  7. Linguagem: `sanitizePastoralLanguage` substitui "reze"→"ore" etc.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Calendar, ArrowRight, Clock } from "lucide-react";
import { api } from "../lib/api";
import useDaily from "../lib/useDaily";
import NewsletterCTA from "./NewsletterCTA";
import {
  getInternalThemeContent,
  isLandingContentAlignedWithTheme,
} from "../lib/weeklyThemeContent";
import { computeWeekDates } from "../lib/weekDates";
import { formatVerse } from "../lib/verseFormat";
import { sanitizePastoralLanguage } from "../lib/pastoralLanguage";
import { recordEvent } from "../lib/retention";
import { BibleRefLink } from "../lib/bibleRef";

const THEME_TITLE = {
  oracao: "Semana de Oração",
  familia: "Semana da Família",
  esperanca: "Semana da Esperança",
  perdao: "Semana do Perdão",
  paz: "Semana da Paz",
  proposito: "Semana do Propósito",
  gratidao: "Semana da Gratidão",
  identidade: "Semana da Identidade",
  cura: "Semana da Cura",
};

function themeToTitle(themeSlug, fallback) {
  if (!themeSlug) return fallback || "Semana de Oração";
  if (THEME_TITLE[themeSlug]) return THEME_TITLE[themeSlug];
  const t = String(themeSlug);
  return `Semana da ${t.charAt(0).toUpperCase()}${t.slice(1)}`;
}

/** Aplica todos os saneadores de linguagem/refs a um dia. */
function refineDay(d) {
  if (!d || typeof d !== "object") return d;
  return {
    ...d,
    title: sanitizePastoralLanguage(d.title || ""),
    verse_ref: formatVerse(d.verse_ref || d.verse || ""),
    focus: sanitizePastoralLanguage(d.focus || d.theme || ""),
    reflection: sanitizePastoralLanguage(d.reflection || d.prayer_guide || ""),
    prayer_direction: sanitizePastoralLanguage(d.prayer_direction || ""),
    challenge: sanitizePastoralLanguage(d.challenge || ""),
  };
}

export default function PrayerWeekCard({ compact = false, todayOnly = false }) {
  const [state, setState] = useState({ loading: true, available: false, data: null });
  const [themeSlug, setThemeSlug] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const { daily } = useDaily();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/integrations/prayer-week").catch(() => null),
      api.get("/integrations/weekly-theme").catch(() => null),
    ]).then(([weekRes, themeRes]) => {
      if (cancelled) return;
      if (weekRes && weekRes.data) {
        setState({
          loading: false,
          available: !!weekRes.data.available,
          data: weekRes.data.data || null,
          message: weekRes.data.message || "",
        });
      } else {
        setState({ loading: false, available: false, data: null, message: "Semana de Oração em breve" });
      }
      if (themeRes && themeRes.data && themeRes.data.available && themeRes.data.theme) {
        setThemeSlug(themeRes.data.theme);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // week_start vem do useDaily().weekly_theme (mesma fonte da Home)
  const weekStart = daily?.weekly_theme?.week_start || null;
  const weekDates = useMemo(() => computeWeekDates(weekStart), [weekStart]);

  // Detecção de mismatch e escolha do conteúdo a renderizar.
  // Landing days = state.data?.days. Se mismatch → tenta override interno.
  const contentDecision = useMemo(() => {
    const landingDays = Array.isArray(state.data?.days) ? state.data.days : [];
    const alignment = isLandingContentAlignedWithTheme(themeSlug, landingDays);
    // alignment: true → landing OK · false → mismatch · null → sem info
    if (alignment === false) {
      const internal = getInternalThemeContent(themeSlug);
      if (internal && Array.isArray(internal.days) && internal.days.length === 7) {
        return {
          source: "internal-override",
          days: internal.days.map(refineDay),
          title: internal.title,
          description: internal.description,
        };
      }
      // Mismatch sem override interno → OCULTA (anti-mistura)
      return { source: "hidden-mismatch", days: [], title: null, description: null };
    }
    // alignment true ou null → usa landing como está (sanitizada)
    return {
      source: "landing",
      days: landingDays.map(refineDay),
      title: null,
      description: null,
    };
  }, [state.data, themeSlug]);

  // Log técnico quando override ativa (transparência p/ suporte + monitoramento
  // do bug externo da landing /api/prayer-week).
  useEffect(() => {
    if (contentDecision.source === "internal-override") {
      console.info(
        "[PrayerWeekCard] internal override ATIVO",
        {
          theme_slug: themeSlug,
          reason: "landing /api/prayer-week desalinhado com weekly-theme",
          landing_title: state.data?.title,
          override_title: contentDecision.title,
          note: "mitigação temporária — remover quando landing sincronizar",
        },
      );
    } else if (contentDecision.source === "hidden-mismatch") {
      console.warn(
        "[PrayerWeekCard] SEÇÃO OCULTA (anti-mistura)",
        {
          theme_slug: themeSlug,
          reason: "mismatch entre título e conteúdo, sem override interno disponível",
        },
      );
    }
  }, [contentDecision.source, contentDecision.title, themeSlug, state.data?.title]);

  if (state.loading) {
    // Não renderizar skeleton com espaço visível — se card não tiver conteúdo
    // aprovado, deve ficar totalmente oculto (silêncio elegante, spec Carlos
    // P0.1). O loading fica em <section hidden> para não reservar layout.
    return null;
  }

  // Anti-mistura absoluta: tema conhecido, conteúdo desalinhado, sem override.
  // Ocultamos a seção completa (retorna null → nada renderiza).
  if (contentDecision.source === "hidden-mismatch") {
    return null;
  }

  // Filtro central: se landing endpoint não está disponível ou não tem days
  // válidos, oculta completamente (sem placeholder, sem "em breve").
  const hasValidDays = Array.isArray(contentDecision.days) && contentDecision.days.length > 0;
  if (!state.available || !hasValidDays) {
    return null;
  }

  const d = state.data || {};
  // Fonte da verdade do nome: weekly_theme.theme_name vem do banco da landing
  // (mesma fonte do site). O mapa de slugs fica apenas como fallback.
  const displayTitle =
    daily?.weekly_theme?.theme_name ||
    themeToTitle(themeSlug, contentDecision.title || d.title);
  const description = contentDecision.description || d.description;
  const days = contentDecision.days;
  const isOverride = contentDecision.source === "internal-override";

  return (
    <section
      data-testid="home-prayer-week-section"
      className="mt-8"
    >
      <div
        data-testid="prayer-week-card"
        data-theme={themeSlug || "oracao"}
        data-source={contentDecision.source}
        className="rounded-2xl border border-gold/40 bg-[hsl(var(--parchment))] px-5 py-5 sm:p-6"
      >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-gold" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold" data-testid="prayer-week-card-eyebrow">
            {displayTitle}
          </p>
          <p
            className="mt-1 font-display text-xl sm:text-2xl text-navy leading-snug"
            data-testid="prayer-week-card-title"
          >
            {displayTitle}
          </p>
          {d.subtitle && (
            <p className="mt-1 text-sm text-navy/70 leading-relaxed">{d.subtitle}</p>
          )}
          {description && !compact && (
            <p className="mt-2 text-sm text-navy/70 leading-relaxed whitespace-pre-line">
              {sanitizePastoralLanguage(description)}
            </p>
          )}

          {!compact && Array.isArray(days) && days.length > 0 && (() => {
            // N1 — Regra "apenas o dia atual" na Home pública (prompt master
            // reordenação teológica). Filtra para renderizar somente o dia que
            // bate com hoje em America/Recife via weekDates.isToday.
            const visibleDays = todayOnly
              ? days.map((d, i) => ({ d, i })).filter(({ i }) => weekDates[i]?.isToday).map(({ d }) => d)
              : days.slice(0, 7);
            if (todayOnly && visibleDays.length === 0) {
              // Não há dia da campanha correspondente a hoje → oculta bloco.
              return null;
            }
            return (
            <>
            <ol
              data-testid="prayer-week-days"
              data-override={isOverride ? "true" : "false"}
              data-today-only={todayOnly ? "true" : "false"}
              className={
                todayOnly
                  ? "mt-4 grid grid-cols-1 gap-2.5"
                  : "mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5"
              }
            >
              {visibleDays.slice(0, todayOnly ? 1 : 7).map((day, i) => {
                // Quando todayOnly, precisamos do índice do dia atual dentro
                // da lista original para descobrir a data. Reobtém do days[].
                const origIndex = todayOnly
                  ? days.findIndex((x) => x === day)
                  : i;
                const dt = weekDates[origIndex] || null;
                const isToday = !!dt?.isToday;
                const header = dt?.header || `Dia ${day.day || origIndex + 1}`;
                return (
                  <li
                    key={day.day || origIndex + 1}
                    data-testid={`prayer-week-day-${day.day || origIndex + 1}`}
                    data-today={isToday ? "true" : "false"}
                    data-past={dt?.isPast ? "true" : "false"}
                    className={`rounded-xl px-3.5 py-3 text-sm transition-colors ${
                      isToday
                        ? "border-2 border-gold bg-gold/10"
                        : dt?.isPast
                        ? "border border-navy/10 bg-white/70"
                        : "border border-navy/10 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-navy/60">
                      <Calendar className="w-3 h-3" />
                      <span>{header}</span>
                      {isToday && (
                        <span className="ml-auto text-gold font-semibold tracking-widest">Hoje</span>
                      )}
                    </div>
                    {day.title && (
                      <p className="mt-1 font-medium text-navy leading-snug">{day.title}</p>
                    )}
                    {day.verse_ref && (
                      <BibleRefLink
                        refText={day.verse_ref}
                        // Sprint refinamento — F: tipografia mais séria para
                        // referências bíblicas. tabular-nums impede que
                        // dígitos "dancem" e reduz confusão visual entre
                        // letras (Sl) e algarismos (11) em Manrope.
                        //
                        // P0 (iter63): agora clicável — clique primário abre
                        // capítulo na Bíblia; compartilhamento é ação separada.
                        className="mt-0.5 inline-block text-xs text-gold tabular-nums font-medium hover:underline hover:text-navy transition-colors"
                        testid={`prayer-week-day-${day.day || i + 1}-verse`}
                      />
                    )}
                    {day.focus && (
                      <p className="mt-1.5 text-[13px] text-navy/75 leading-relaxed italic" data-testid={`prayer-week-day-${day.day || origIndex + 1}-focus`}>
                        {day.focus}
                      </p>
                    )}
                    {day.challenge && (
                      <p className="mt-2 pt-2 border-t border-navy/8 text-[12px] text-navy/70 leading-relaxed" data-testid={`prayer-week-day-${day.day || origIndex + 1}-challenge`}>
                        <span className="text-[10px] uppercase tracking-widest text-gold">Desafio </span>
                        {day.challenge}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
            {todayOnly && (
              <div className="mt-3">
                <Link
                  to="/semana"
                  data-testid="prayer-week-see-full"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/65 hover:text-gold transition-colors"
                >
                  Ver semana completa
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
            </>
            );
          })()}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                recordEvent("click", "prayer-week-signup-toggle");
                setShowSignup((v) => !v);
              }}
              data-testid="prayer-week-signup-toggle"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-4 py-2 text-[11px] uppercase tracking-[0.25em] hover:bg-navy/90 transition-colors"
            >
              Participar por e-mail
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {d.signup_url && (
              <a
                href={d.signup_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs uppercase tracking-[0.2em] text-navy/65 hover:text-gold"
              >
                Saiba mais
              </a>
            )}
          </div>

          {showSignup && (
            <div className="mt-4" data-testid="prayer-week-signup-form">
              <NewsletterCTA
                interest="prayer-week"
                title={`Inscreva-se na ${displayTitle}`}
                subtitle="Você receberá o guia diário e os lembretes por e-mail."
                testidPrefix="prayer-week-newsletter"
              />
            </div>
          )}
        </div>
      </div>
      </div>
    </section>
  );
}
